import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Central screen navigation backed by an explicit history stack.
 *
 * `nav()` pushes the target screen; `back()` pops the current screen and
 * returns to the prior one, falling back to Home when the in-app stack is
 * empty (e.g. a deep-link straight into an inner screen). The stack is kept in
 * sync with the browser history — forward navigations grow it, `popstate`
 * (browser/hardware Back) shrinks it — so the Back button behaves identically.
 */
@Injectable({ providedIn: 'root' })
export class NavService {
  /** Visited screen URLs, oldest → current (top of stack). */
  private readonly stack: string[] = [];
  private lastTrigger: 'imperative' | 'popstate' | 'hashchange' = 'imperative';

  constructor(private router: Router, private location: Location) {
    this.router.events
      .pipe(filter((e): e is NavigationStart | NavigationEnd => e instanceof NavigationStart || e instanceof NavigationEnd))
      .subscribe((e) => {
        if (e instanceof NavigationStart) {
          this.lastTrigger = e.navigationTrigger ?? 'imperative';
          return;
        }
        // NavigationEnd: reconcile the stack with what actually navigated.
        if (this.lastTrigger === 'popstate') {
          if (this.stack.length > 1) this.stack.pop();
        } else if (this.stack[this.stack.length - 1] !== e.urlAfterRedirects) {
          this.stack.push(e.urlAfterRedirects);
        }
      });
  }

  /** Depth of the in-app navigation stack (1 = only the current screen). */
  get depth(): number { return this.stack.length; }

  /** Whether there is a prior in-app screen to return to. */
  get canGoBack(): boolean { return this.stack.length > 1; }

  /** Navigate to a top-level screen id (e.g. 'menu'), pushing it onto the stack. */
  nav(screen: string): void { this.router.navigate(['/', screen]); }

  /** Pop back to the previous screen, or Home when the stack is empty. */
  back(): void {
    if (this.canGoBack) this.location.back();
    else this.router.navigate(['/home']);
  }
}
